import axios from "axios";
import React, {
  createContext,
  useState,
  useEffect,
  ReactNode,
  useRef,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

interface Signin {
  success: boolean;
  data: User;
}

interface AuthVerifyResponse {
  loggedIn: boolean;
  user: User;
}


export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  role: string;
  clinic: string;
  firstAuth: boolean;
  accountStatus: string;
  createdAt: string;
  updatedAt: string;
  praticienId: string | null;
}

interface AuthContextType {
  userContext: User | null;
  isAuth: boolean;
  signin: ({
    email,
    password,
  }: {
    email: string;
    password: string;
  }) => Promise<Signin>;
  logout: () => Promise<boolean | Error>;
  checkIfUserLoggedIn: () => Promise<void>;
  setUserContext: React.Dispatch<React.SetStateAction<User | null>>;
}

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined
);

interface AuthProviderProps {
  children: ReactNode;
}

const getUserFromLocalStorage = () => {
  const userString = localStorage.getItem("user");
  if (userString === null) {
    // console.log("No user in local storage");
    return null; // Directly return null if there is no user data.
  }
  try {
    return JSON.parse(userString);
  } catch (error) {
    console.error("Error parsing user data:", error);
    return null; // Return null if JSON parsing fails.
  }
};

const setUserToLocalStorage = (userData: User | null) => {
  if (userData) {
    try {
      const userString = JSON.stringify(userData);
      localStorage.setItem("user", userString);
    } catch (error) {
      console.error("Error saving user data:", error);
    }
  } else {
    localStorage.removeItem("user");
  }
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [userContext, setUserContext] = useState<User | null>(
    getUserFromLocalStorage()
  );
  const [isAuth, setIsAuth] = useState<boolean>(
    !!localStorage.getItem("isAuth")
  );
  const navigate = useNavigate();

  const INACTIVITY_TIME_LIMIT = 15 * 60 * 1000; // 15 minutes en millisecondes
  const inactivityTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const resetTimer = () => {
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
    }
    inactivityTimeoutRef.current = setTimeout(() => {
      // logout();
      checkIfUserLoggedIn();
    }, INACTIVITY_TIME_LIMIT);
  };

  

  useEffect(() => {
    if (isAuth) {
      const events = [
        "mousemove",
        "mousedown",
        "keypress",
        "scroll",
        "touchstart",
      ];

      const handleActivity = () => {
        resetTimer();
      };

      events.forEach((event) => {
        window.addEventListener(event, handleActivity);
      });

      // Démarrer le timer d'inactivité
      resetTimer();

      // Nettoyer les écouteurs à la désinstallation du composant
      return () => {
        if (inactivityTimeoutRef.current) {
          clearTimeout(inactivityTimeoutRef.current);
        }
        events.forEach((event) => {
          window.removeEventListener(event, handleActivity);
        });
      };
    }
  }, [isAuth]);

  useEffect(() => {
    setUserToLocalStorage(userContext);
  }, [userContext]);

  // const checkIfUserLoggedIn = async () => {
  //   try {
  //     const { data } = await axios.post<User>(`/common/auth/verify`);
  //     if (data && data.accountStatus === "ACTIF") {
  //       setUserContext(data);
  //       setIsAuth(true);
  //     } else {
  //       throw new Error("Utilisateur non actif");
  //     }
  //   } catch (error) {
  //     console.log("error", error);
  //     setIsAuth(false);
  //     navigate("/connexion");
  //     // toast.error("Session expirée ou invalide. Veuillez vous reconnecter.");
  //   }
  // };

  const checkIfUserLoggedIn = async () => {
    // console.log("Checking if user is logged in...", !userContext && isAuth, !userContext, isAuth);
    if (!userContext &&  isAuth) {
      try {
        const { data } = await axios.post<AuthVerifyResponse>(`/common/auth/verify`);
        // console.log("User data from verify endpoint:", data);
        if (data && data.user.accountStatus === "ACTIF" && data.loggedIn === true) {
          setUserContext(data.user);
          setIsAuth(true);
          localStorage.setItem("user", JSON.stringify(data));
          localStorage.setItem("isAuth", "true");
        } else {
          throw new Error("Utilisateur non actif");
        }
      } catch (error) {
        // console.log("error", error);

        setIsAuth(false);
        setUserContext(null);
        localStorage.removeItem("user");
        localStorage.removeItem("isAuth");
        navigate("/connexion", { replace: true });
        toast.error("Session expirée ou invalide. Veuillez vous reconnecter.");
      }
    }
  };

  const checkIfUserLoggedInWithContext = async () => {
    // console.log("Checking if user is logged in with context...", userContext);
    if (userContext) {
      try { 
        // console.log("User context exists, checking session...");
        const { data } = await axios.post<AuthVerifyResponse>(`/common/auth/verify`);
        // console.log("User data from verify endpoint:", data);
        if (data && (data.loggedIn === false)) {
          setIsAuth(false);
          setUserContext(null);
          localStorage.removeItem("user");
          localStorage.removeItem("isAuth");
          logout();
          //navigate("/connexion", { replace: true });
          // toast.error("Session expirée ou invalide. Veuillez vous reconnecter.");
        }
      } catch (error) {
        // console.log("error", error);
        if (axios.isAxiosError(error) && error.response?.data.loggedIn === false) {
          setIsAuth(false);
          setUserContext(null);
          localStorage.removeItem("user");
          localStorage.removeItem("isAuth");
          logout();
        }
      }
    }
    };

  useEffect(() => {
    checkIfUserLoggedInWithContext();
    checkIfUserLoggedIn();
  }, [userContext, isAuth]);

  const signin = async ({
    email,
    password,
  }: {
    email: string;
    password: string;
  }): Promise<Signin> => {
    // Assurez-vous de retourner une promesse du type attendu (ici, `User`)
    const config = {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    };
    const body = { email, password };

    try {
      const { data: dataResponse } = await axios.post(
        `/common/auth/login`,
        body,
        config
      );

      // console.log("dataResponse", dataResponse);

      if (dataResponse && dataResponse.data.role) {
        setIsAuth(true);
        setUserContext(dataResponse.data);
        setUserToLocalStorage(dataResponse.data);
        localStorage.setItem("isAuth", "true");

        toast.success("Vous êtes connecté", {
          position: "top-center",
          autoClose: 1000,
        });

        return dataResponse;
      } else {
        throw new Error("Réponse invalide du serveur");
      }
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        // console.log("error", error);
        const errorMessage =
          error.response?.data.error || "Une erreur inattendue s'est produite.";

        return Promise.reject(new Error(errorMessage));
      } else if (error instanceof Error) {
        return Promise.reject(error);
      } else {
        return Promise.reject(
          new Error("Une erreur inattendue s'est produite.")
        );
      }
    }
  };

  const logout = useCallback(async (): Promise<boolean | Error> => {
    try {
      await axios.post("/common/auth/logout"); // Appel au backend pour déconnecter l'utilisateur
      setIsAuth(false);
      setUserContext(null);
      localStorage.removeItem("user");
      localStorage.removeItem("isAuth");
      navigate("/connexion");
      toast.success("Vous avez été déconnecté avec succès.", {
        position: "top-center",
        autoClose: 1000,
      });
      return true;
    } catch (error) {
      toast.error("Erreur lors de la déconnexion. Veuillez réessayer.", {
        position: "top-center",
        autoClose: 2500,
      });
      return error as Error;
    }
  }, [navigate, setIsAuth, setUserContext, setUserToLocalStorage]);

  const value = {
    userContext,
    isAuth,
    signin,
    logout,
    checkIfUserLoggedIn,
    setUserContext,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
